// Derivación de salario para Costa Rica.
// Convención (alineada con el motor de planilla):
//   - 30 días por mes, jornada ordinaria de 8 h/día (240 h/mes).
//   - Salario por hora = diario / 8 = mensual / 240.
//   - Hora extra = hora ordinaria × multiplicador (1.5 por defecto).

const DAYS_PER_MONTH = 30;
const ORDINARY_HOURS_PER_DAY = 8;

/**
 * @param {number} amount  Monto ingresado.
 * @param {"monthly"|"daily"} salaryType  Fijación salarial.
 * @param {object} cfg  { overtime_multiplier, work_hours_per_month }
 * @returns {{monthly:number, daily:number, hourly:number, overtimeHour:number, overtimeMult:number, daysPerMonth:number, hoursPerMonth:number}}
 */
export function deriveSalary(amount, salaryType, cfg = {}) {
  const value = Number(amount) || 0;
  const overtimeMult = Number(cfg.overtime_multiplier) || 1.5;
  const daysPerMonth = DAYS_PER_MONTH;
  const hoursPerMonth = daysPerMonth * ORDINARY_HOURS_PER_DAY;

  let monthly;
  let daily;
  if (salaryType === "daily") {
    daily = value;
    monthly = value * daysPerMonth;
  } else {
    monthly = value;
    daily = value / daysPerMonth;
  }
  const hourly = daily / ORDINARY_HOURS_PER_DAY;
  const overtimeHour = hourly * overtimeMult;

  return {
    monthly: round2(monthly),
    daily: round2(daily),
    hourly: round2(hourly),
    overtimeHour: round2(overtimeHour),
    overtimeMult,
    daysPerMonth,
    hoursPerMonth,
  };
}

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

// ── Estimación de horas extra mensuales ──────────────────────────────────────
const WEEKLY_ORD_LIMIT = { JOD: 48, JOM: 42, JON: 36, JOA: 48, JOE: 48 };
export const WEEKS_PER_MONTH = 4.33;

function hhmmToMin(s) {
  if (!s) return null;
  const [h, m] = String(s).split(":");
  const v = Number(h) * 60 + Number(m || 0);
  return Number.isFinite(v) ? v : null;
}

/** Horas extra por semana = total laborado − tope semanal de ley. */
export function weeklyExtraHours(daySchedules = {}, journeyType = "JOD") {
  let total = 0;
  for (const sch of Object.values(daySchedules || {})) {
    if (!sch) continue;
    const a = hhmmToMin(sch.start);
    const b = hhmmToMin(sch.end);
    if (a === null || b === null) continue;
    let dur = b - a;
    if (dur < 0) dur += 24 * 60;
    total += dur / 60;
  }
  const limit = WEEKLY_ORD_LIMIT[journeyType] || 48;
  return Math.max(0, total - limit);
}

/**
 * Estima la ganancia mensual por horas extra:
 *   horas extra/semana × 4.33 semanas/mes × precio hora extra.
 */
export function monthlyOvertimeEstimate({ daySchedules, journeyType, overtimeHour }) {
  const weekExtra = weeklyExtraHours(daySchedules, journeyType);
  const monthlyExtraHours = round2(weekExtra * WEEKS_PER_MONTH);
  const total = round2(monthlyExtraHours * (Number(overtimeHour) || 0));
  return { weekExtra: round2(weekExtra), weeksPerMonth: WEEKS_PER_MONTH, monthlyExtraHours, total };
}

export function formatCRC(val) {
  const n = Number(val) || 0;
  return `\u20A1${n.toLocaleString("es-CR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
